"use client";
import React, { useRef } from "react";
import { cn } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import dynamic from "next/dynamic";
import FinalScore from "../../FinalScore";
import { TokenInfo } from "./TokenInfo";
import { PriceSection } from "./PriceSection";
import { ChartSection } from "./ChartSection";
import { ActionButtons } from "./ActionButtons";
import { TokenPopoverProps, PriceChangeStatus } from "./types";
import Translations from "@translate/components/Chat/TokenPopover/TokenPopover";
import { I18nUtil } from "@utils/i18n";

const Popover = dynamic(
  () => import("../../Popover").then((mod) => mod.Popover),
  {
    ssr: false,
  },
);

const I18n = I18nUtil.createScoped(Translations);

export const TokenPopover: React.FC<TokenPopoverProps> = ({
  children,
  data,
  onForecastClick,
  disabled = false,
  className,
  contentClassName,
  handleViewReport,
  onVisibilityChange,
}) => {
  const ref = useRef<any>(null);
  if (!data || disabled) {
    return children;
  }
  // 获取当前价格的涨跌状态（用于图表颜色）
  const currentPriceChangeStatus = data.price.change.status;

  // 光晕效果颜色映射（与图表颜色一致）
  const glowColorMapping: Record<PriceChangeStatus, string> = {
    up: ospThemes.colors.content_accent1_primary, // 涨：绿色
    down: ospThemes.colors.content_accent2_primary, // 跌：红色
    flat: ospThemes.colors.content_tertiary, // 平：灰色
  };
  const glowColor = glowColorMapping[currentPriceChangeStatus];

  // 移除priceItems转换逻辑，直接使用price数据
  const content = (
    <div
      className={cn(
        // 布局
        "inline-flex flex-col items-end gap-8 p-6 relative",
        // 外观
        "bg-background_solid_tertiary rounded-3 overflow-hidden",
        "border border-solid border-border_white_transparent10",
        // 宽度固定，基于原始设计
        "w-[554px]",
        contentClassName,
      )}
    >
      {/* 背景光晕效果 */}
      <div
        className={cn(
          "absolute w-[190px] h-[129px] top-[230px] left-[-142px]",
          "rounded-full opacity-50",
          "blur-[141px]",
        )}
        style={{ backgroundColor: glowColor }}
      />

      {/* 顶部区域：头像信息和评分 */}
      <div className={cn("flex items-center justify-between w-full")}>
        <TokenInfo token={data.token} />
        {data?.score?.score && (
          <FinalScore
            score={data.score.score}
            laurelWidth={17}
            laurelHeight={52}
            classNames={{
              container: "gap-2",
              text: "body_medium_m",
              score: "headline_regular_m",
            }}
          />
        )}
      </div>

      {/* 价格和图表区域 */}
      <div className={cn("flex w-full items-center justify-between")}>
        <PriceSection title={I18n.t("Current Price")} price={data.price} />
        {data.chart && (
          <ChartSection
            chart={data.chart}
            changeStatus={currentPriceChangeStatus}
          />
        )}
      </div>

      {/* 底部按钮区域 */}
      <ActionButtons
        isHKStock={data.price.current.currency !== "USD"}
        symbol={data.token.symbol}
        showReport={!!data.score} // 根据score字段存在与否控制显示
        onForecastClick={onForecastClick}
        onReportClick={(e) => {
          handleViewReport?.(e);
          ref.current.close();
        }}
      />
    </div>
  );

  return (
    <Popover
      ref={ref}
      content={content}
      disabled={disabled}
      className={className}
      delay={150}
      placement="bottom-start"
      onVisibilityChange={onVisibilityChange}
    >
      {children}
    </Popover>
  );
};
